import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3, Plus, Edit, Trash2, Eye, Download, Loader2, FileText, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReportsPage() {
  const [reports, setReports] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // Generate form state
  const [showGenerator, setShowGenerator] = useState(false);
  const [newReport, setNewReport] = useState({
    sector: '',
    period: 'Q1-2026',
    title: ''
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchReports();
    fetchSectors();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports || []);
    } catch (err) {
      setError('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/reports/sectors`);
      setSectors(response.data.sectors || []);
    } catch (err) {
      console.error('Failed to fetch sectors');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!newReport.sector) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/reports/generate`,
        newReport,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReports([response.data.report, ...reports]);
      setShowGenerator(false);
      setNewReport({ sector: '', period: 'Q1-2026', title: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(reports.filter(r => r.id !== reportId));
    } catch (err) {
      setError('Failed to delete report');
    }
  };

  const handlePublish = async (reportId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/reports/${reportId}`,
        { status: 'published' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReports();
    } catch (err) {
      setError('Failed to publish report');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-800">Published</Badge>;
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-800">Draft</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-800">Archived</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trend Reports</h1>
            <p className="text-gray-600">Generate and manage AI-powered industry insights</p>
          </div>
          <Button onClick={() => setShowGenerator(!showGenerator)}>
            <Plus className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Generator Form */}
        {showGenerator && (
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                Generate New Report
              </CardTitle>
              <CardDescription>
                AI will analyze event data and generate insights for the selected sector
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="sector">Sector *</Label>
                    <Select
                      value={newReport.sector}
                      onValueChange={(value) => setNewReport({ ...newReport, sector: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((s) => (
                          <SelectItem key={s.name} value={s.name}>
                            {s.name} ({s.eventCount} events)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="period">Period *</Label>
                    <Select
                      value={newReport.period}
                      onValueChange={(value) => setNewReport({ ...newReport, period: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Q1-2026">Q1 2026</SelectItem>
                        <SelectItem value="Q2-2026">Q2 2026</SelectItem>
                        <SelectItem value="Q3-2026">Q3 2026</SelectItem>
                        <SelectItem value="Q4-2026">Q4 2026</SelectItem>
                        <SelectItem value="2026">Full Year 2026</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="title">Custom Title (optional)</Label>
                    <Input
                      id="title"
                      value={newReport.title}
                      onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button type="submit" disabled={generating || !newReport.sector}>
                    {generating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate Report</>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowGenerator(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Reports List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              All Reports ({reports.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No reports yet. Generate your first report above.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-900">{report.title}</h3>
                        {getStatusBadge(report.status)}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {report.sector} · {report.period} · {report.data?.totalEvents || 0} events analyzed
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span><Eye className="w-3 h-3 inline mr-1" />{report.stats?.views || 0} views</span>
                        <span><Download className="w-3 h-3 inline mr-1" />{report.stats?.downloads || 0} downloads</span>
                        <span>Created {new Date(report.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {report.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePublish(report.id)}
                        >
                          Publish
                        </Button>
                      )}
                      {report.status === 'published' && (
                        <Link to={`/insights/${report.slug}`} target="_blank">
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                      <Link to={`/admin/reports/${report.id}`}>
                        <Button size="sm" variant="outline">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(report.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
